import React, { FunctionComponent } from "react";
import { Pattern } from "../Pattern";
import Section from "../layout/Section";
import { TitleHeading } from "../typography/TitleHeading";
import BodyText from "../typography/BodyText";
import { useAnimation } from "../../hooks/useAnimate";
import { twMerge } from "tailwind-merge";

type GridBlockProps = {
  tag: string;
  title: JSX.Element | string;
  description?: JSX.Element | string;
  blocks: React.ReactNode[];
  lg3?: boolean;
  singleColumn?: boolean;
  theme?: "dark" | "white" | "gray";
  noMargin?: "no-margin" | "top" | "bottom";
  mobileMultiCol?: boolean;
};

const GridBlock: FunctionComponent<GridBlockProps> = ({
  tag,
  title,
  description,
  blocks,
  lg3 = true,
  singleColumn = false,
  theme = "white",
  noMargin,
  mobileMultiCol,
}) => {
  const { id, animate } = useAnimation();

  return (
    <Section theme={theme} noMargin={noMargin} id={id}>
      <div {...animate("fade-down")}>
        <TitleHeading
          tag={tag}
          title={title}
          theme={theme}
          center={false}
          bgTag={true}
        />
        {description && (
          <BodyText className="max-w-4xl" mb={0}>
            {description}
          </BodyText>
        )}
      </div>
      <div
        className={twMerge(
          `w-full grid  gap-1 mt-12`,
          mobileMultiCol ? "grid-cols-2" : "grid-cols-1",
          !singleColumn ? (lg3 ? "lg:grid-cols-3" : "md:grid-cols-2") : "",
        )}
      >
        {blocks.map((block, i) => (
          <div
            key={`block${i}`}
            {...animate(i % 2 === 0 ? "fade-right" : "fade-left", 100 * i)}
            className={`${
              theme === "dark"
                ? "bg-gray-800 hover:bg-gray-700"
                : theme === "white"
                  ? "bg-gray-100 hover:bg-gray-200 dark:bg-gray-900 dark:hover:bg-gray-800"
                  : "bg-gray-200 hover:bg-gray-300 dark:bg-gray-800 dark:hover:bg-gray-700"
            } p-6 transition-colors duration-200`}
          >
            {block}
          </div>
        ))}
      </div>
      <Pattern
        type="square"
        className={`hidden lg:block absolute h-1/3 w-1/6 top-0 right-0 ${
          theme === "dark"
            ? "text-gray-800"
            : "text-gray-200 dark:text-gray-800 opacity-70"
        }`}
      ></Pattern>
    </Section>
  );
};

export default GridBlock;
